import React, { Component } from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { TEXT_COLORS } from '../utils/colors';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2,
            p: 3,
            textAlign: 'center',
          }}
        >
          <ErrorOutline sx={{ fontSize: 64, color: TEXT_COLORS.MUTED }} />
          <Typography variant="h5" fontWeight={700} sx={{ color: '#ffffff' }}>
            Something went wrong
          </Typography>
          <Typography sx={{ color: TEXT_COLORS.MUTED, maxWidth: 400 }}>
            An unexpected error occurred. Please try again.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={this.handleReset}
              sx={{ color: TEXT_COLORS.MUTED, borderColor: TEXT_COLORS.MUTED }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={this.handleReload}
              sx={{ color: TEXT_COLORS.MUTED, borderColor: TEXT_COLORS.MUTED }}
            >
              Reload Page
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
